import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import SeoCopy from "@/components/SeoCopy";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const next = params.get("next") || "/schedule";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", just_moved_in: false, is_adult: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (refFromUrl) localStorage.setItem("bd_ref", refFromUrl.toUpperCase());
  }, [refFromUrl]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.is_adult) {
      toast.error("You must be 18 or older to create an account.");
      return;
    }
    setLoading(true);
    try {
      const { just_moved_in, is_adult, ...payload } = form;
      const user = await register(payload);
      if (just_moved_in) {
        try {
          await (await import("@/lib/api")).default.put("/users/me/move-in");
          toast.success("🎉 Welcome — your first pickup is on us!");
        } catch { /* ignore */ }
      }
      toast.success(`Welcome, ${user.name}! Your account is ready.`);
      navigate(next);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <div className="card-clay p-7">
        <h1 className="font-fredoka text-3xl font-bold text-slate-900" data-testid="register-title">Create your account</h1>
        <p className="text-slate-600 text-sm mt-1">Track pickups, earn Dash Rewards, and get your own referral code.</p>
        <form onSubmit={onSubmit} className="mt-5 space-y-3">
          <Field label="Full name"><input required className="input-clay h-11 px-3 w-full" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="register-name" /></Field>
          <Field label="Email"><input type="email" required className="input-clay h-11 px-3 w-full" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="register-email" /></Field>
          <Field label="Phone (optional)"><input className="input-clay h-11 px-3 w-full" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="register-phone" /></Field>
          <Field label="Password (min 6 chars)"><input type="password" required minLength={6} className="input-clay h-11 px-3 w-full" value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="register-password" /></Field>
          <label className="flex items-center gap-3 p-3 rounded-2xl bg-amber-50 border-2 border-amber-200 cursor-pointer" data-testid="register-movein">
            <input type="checkbox" checked={form.just_moved_in} onChange={(e) => upd("just_moved_in", e.target.checked)} className="w-5 h-5 accent-amber-500"/>
            <span className="text-sm font-semibold text-amber-900">🎉 I just moved in! (First pickup on us)</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border-2 border-slate-200 cursor-pointer" data-testid="register-adult">
            <input type="checkbox" required checked={form.is_adult} onChange={(e) => upd("is_adult", e.target.checked)} className="w-5 h-5 accent-green-500"/>
            <span className="text-sm font-semibold text-slate-700">I confirm I am 18 years or older.</span>
          </label>
          <button type="submit" disabled={loading} className="btn-push-green w-full py-3" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>
        <div className="text-sm text-slate-600 mt-4 text-center">
          Already have one? <Link to="/login" className="text-green-700 font-bold underline" data-testid="register-to-login">Log in</Link>
        </div>
        <SeoCopy
          title="Sign up for Bag Dash Kids"
          links={[
            { to: "/about", label: "Meet the Kids" },
            { to: "/policies", label: "Pricing & policies" },
            { to: "/rules", label: "Service rules" },
            { to: "/reviews", label: "Customer reviews" },
          ]}
        >
          <p>Creating a free Bag Dash Kids account unlocks instant scheduling for one-time and weekly trash bag pickups, a personalized Dash Rewards loyalty balance, your own referral code (3 paid referrals = 1 free pickup), and access to monthly bin cleaning add-ons for loyal customers.</p>
          <p>New-mover bonus: customers who just moved into a served community earn <strong>10 bonus Dash Points</strong> on signup. Verification is fingerprint-gated so each household qualifies once.</p>
        </SeoCopy>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
